import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { paymentsAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  CreditCard, Calendar, Download, ExternalLink, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Clock, FileText, Loader2, ArrowUpRight, Shield
} from 'lucide-react';

const Billing = () => {
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const res = await paymentsAPI.getBillingDetails();
      setBillingData(res.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await paymentsAPI.getBillingPortal();
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const res = await paymentsAPI.createCheckout(planId, window.location.origin);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await paymentsAPI.cancelSubscription();
      toast.success('Subscription will be cancelled at the end of your billing period');
      setShowCancelModal(false);
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await paymentsAPI.reactivateSubscription();
      toast.success('Subscription reactivated successfully');
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      trialing: { color: 'bg-blue-500/20 text-blue-400', icon: Clock },
      past_due: { color: 'bg-amber-500/20 text-amber-400', icon: AlertTriangle },
      canceled: { color: 'bg-red-500/20 text-red-400', icon: XCircle },
      unpaid: { color: 'bg-red-500/20 text-red-400', icon: AlertTriangle },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500/20 text-gray-400', icon: Clock };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border-0 flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  const sub = billingData?.subscription;
  const isPaid = sub && ['active', 'trialing'].includes(sub.status);
  const isCancelling = sub?.cancel_at_period_end;

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6" data-testid="employer-billing">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Billing & Subscription</h1>
            <p className="mt-1 text-[#5B6472] text-sm">Manage your subscription, payment method, and view invoices.</p>
          </div>
          <Button
            onClick={fetchBillingData}
            variant="outline"
            className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5]">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Overview
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Invoices
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* Current Plan Card */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-5 h-5 text-[#1B5AFF]" />
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Current Plan</h2>
              </div>

              {isPaid ? (
                <div className="space-y-6">
                  {/* Plan Details */}
                  <div className="flex flex-col sm:flex-row justify-between gap-4 p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-[#0A0F1E] font-medium capitalize text-lg">
                          {billingData.subscription_tier} Plan
                        </p>
                        {getStatusBadge(sub.status)}
                      </div>
                      <p className="text-[#1B5AFF] text-2xl font-bold">
                        {formatCurrency(sub.price_amount, sub.currency)}/{sub.interval}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      {isCancelling ? (
                        <div>
                          <p className="text-amber-400 text-sm font-medium">Cancels on</p>
                          <p className="text-[#0A0F1E]">{formatDate(sub.current_period_end)}</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-[#5B6472] text-sm">Next billing date</p>
                          <p className="text-[#0A0F1E]">{formatDate(sub.current_period_end)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cancellation Warning */}
                  {isCancelling && (
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        <div>
                          <p className="text-amber-400 font-medium">Subscription ending</p>
                          <p className="text-[#5B6472] text-sm">Your access continues until {formatDate(sub.current_period_end)}</p>
                        </div>
                      </div>
                      <Button
                        onClick={handleReactivate}
                        disabled={reactivating}
                        className="bg-amber-500 hover:bg-amber-600 text-[#0A0F1E] rounded-xl"
                      >
                        {reactivating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Keep Subscription
                      </Button>
                    </div>
                  )}

                  {/* Payment Method */}
                  {billingData.payment_method && (
                    <div className="flex items-center justify-between p-4 rounded-xl bg-[#F5F8FF]/30 border border-[#E8EDF5]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#F5F8FF] flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-[#0A0F1E]" />
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium capitalize">
                            {billingData.payment_method.brand} **** {billingData.payment_method.last4}
                          </p>
                          <p className="text-[#5B6472] text-sm">
                            Expires {billingData.payment_method.exp_month}/{billingData.payment_method.exp_year}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={handleManageBilling}
                        variant="outline"
                        className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
                      >
                        Update
                      </Button>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 pt-2">
                    {billingData.subscription_tier === 'starter' && (
                      <Button
                        onClick={() => handleUpgrade('growth')}
                        className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
                      >
                        <ArrowUpRight className="w-4 h-4 mr-2" />
                        Upgrade to Growth
                      </Button>
                    )}
                    <Button
                      onClick={handleManageBilling}
                      variant="outline"
                      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Manage in Stripe
                    </Button>
                    {!isCancelling && (
                      <Button
                        onClick={() => setShowCancelModal(true)}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl"
                      >
                        Cancel Plan
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                /* No Active Subscription */
                <div className="space-y-4">
                  {billingData.trial_active ? (
                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-blue-400" />
                        <p className="text-blue-400 font-medium">Trial Active</p>
                      </div>
                      <p className="text-[#5B6472] text-sm">
                        Your trial ends on {billingData.trial_end_date ? new Date(billingData.trial_end_date).toLocaleDateString() : 'soon'}. 
                        Subscribe to continue access.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        <p className="text-amber-400 font-medium">No Active Subscription</p>
                      </div>
                      <p className="text-[#5B6472] text-sm">
                        Your trial has ended. Subscribe to regain full access to VETT features.
                      </p>
                    </div>
                  )}

                  {/* Plan Options */}
                  <div className="grid sm:grid-cols-2 gap-4 mt-6">
                    <Card className="bg-[#F5F8FF]/50 border border-[#E8EDF5] rounded-xl p-5">
                      <h3 className="font-semibold text-[#0A0F1E] mb-2">Starter</h3>
                      <p className="text-3xl font-bold text-[#0A0F1E] mb-1">$299<span className="text-sm text-[#5B6472] font-normal">/mo</span></p>
                      <ul className="text-[#5B6472] text-sm space-y-2 my-4">
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Up to 20 records</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 50 talent searches/mo</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Candidate pipeline</li>
                      </ul>
                      <Button onClick={() => handleUpgrade('starter')} variant="outline" className="w-full border-[#1B5AFF]/30 text-[#1B5AFF] hover:bg-[#1B5AFF]/10 rounded-xl">
                        Choose Starter
                      </Button>
                    </Card>
                    <Card className="bg-[#F5F8FF]/50 border-2 border-[#1B5AFF] rounded-xl p-5 relative">
                      <div className="absolute -top-3 left-4 px-2 py-0.5 bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] text-xs font-medium">
                        Popular
                      </div>
                      <h3 className="font-semibold text-[#1B5AFF] mb-2">Growth</h3>
                      <p className="text-3xl font-bold text-[#0A0F1E] mb-1">$799<span className="text-sm text-[#5B6472] font-normal">/mo</span></p>
                      <ul className="text-[#5B6472] text-sm space-y-2 my-4">
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Up to 100 records</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Unlimited searches</li>
                        <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Full analytics suite</li>
                      </ul>
                      <Button onClick={() => handleUpgrade('growth')} className="w-full bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl">
                        Choose Growth
                      </Button>
                    </Card>
                  </div>
                </div>
              )}
            </Card>

            {/* Billing Security */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-green-400" />
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Billing Security</h2>
              </div>
              <p className="text-[#5B6472] text-sm">
                All payments are securely processed through Stripe. VETT never stores your full card details. 
                Your payment information is encrypted and protected by industry-standard security measures.
              </p>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-6">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#E8EDF5] flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#1B5AFF]" />
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Invoice History</h2>
              </div>
              
              {billingData?.invoices?.length > 0 ? (
                <div className="divide-y divide-[#E8EDF5]">
                  {billingData.invoices.map((invoice) => (
                    <div key={invoice.id} className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 hover:bg-[#F5F8FF]/30 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full ${
                          invoice.status === 'paid' ? 'bg-green-400' : 
                          invoice.status === 'open' ? 'bg-amber-400' : 'bg-gray-400'
                        }`} />
                        <div>
                          <p className="text-[#0A0F1E] font-medium">
                            {formatCurrency(invoice.amount_paid || invoice.amount_due, invoice.currency)}
                          </p>
                          <p className="text-[#5B6472] text-sm">
                            {formatDate(invoice.created)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`border-0 ${
                          invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                          invoice.status === 'open' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {invoice.status}
                        </Badge>
                        {invoice.invoice_pdf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="text-[#5B6472] hover:text-[#0A0F1E]"
                          >
                            <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer">
                              <Download className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                        {invoice.hosted_invoice_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="text-[#5B6472] hover:text-[#0A0F1E]"
                          >
                            <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-[#5B6472]/50 mb-4" />
                  <p className="text-[#5B6472]">No invoices yet</p>
                  <p className="text-[#5B6472] text-sm mt-1">Your invoices will appear here once you subscribe</p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription className="text-[#5B6472]">
              Are you sure you want to cancel your subscription?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
              <p className="text-[#0A0F1E] text-sm">
                <strong>What happens when you cancel:</strong>
              </p>
              <ul className="text-[#5B6472] text-sm mt-2 space-y-1 ml-4 list-disc">
                <li>You'll retain access until {sub ? formatDate(sub.current_period_end) : 'the end of your billing period'}</li>
                <li>Your data and records will be preserved</li>
                <li>You can resubscribe anytime</li>
                <li>After expiration, your account reverts to limited access</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
              className="border-[#E8EDF5] text-[#0A0F1E]"
            >
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF] rounded-xl"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Billing;
